import { useEffect } from 'react';
import { Link, Navigate, NavLink, Outlet, useParams } from 'react-router-dom';
import { useMe, useOpenCompany } from '../lib/api';
import { ErrorBox, Skeleton } from '../components/ui';

/** A company's setup, as tabs. AI & history is for app owners only. */
export const COMPANY_TABS: { path: string; label: string; ownerOnly?: boolean }[] = [
  { path: 'team', label: 'Team & workspace' },
  { path: 'integrations', label: 'Integrations' },
  { path: 'journey', label: 'Journey setup' },
  { path: 'rep-scoring', label: 'Rep scoring' },
  { path: 'lead-scoring', label: 'Lead scoring' },
  { path: 'ai', label: 'AI & history', ownerOnly: true },
];

/**
 * Admin → a company. The setup pages work on the company being viewed, so opening another company's
 * setup switches the app to it first (owners), and nothing renders until that has happened.
 */
export function CompanyLayout() {
  const { id = '' } = useParams();
  const me = useMe();
  const open = useOpenCompany();
  const current = me.data?.company.id;
  const isOwner = !!me.data?.user.isOwner;

  useEffect(() => {
    if (current && current !== id && !open.isPending && !open.isError) open.mutate(id);
  }, [current, id, open]);

  if (me.data?.user.role === 'rep') return <Navigate to="/overview" replace />;
  if (open.error) return <ErrorBox error={open.error} />;
  if (!me.data || current !== id) return <Skeleton height={300} />;

  const company = me.data.company;
  return (
    <div className="page">
      {isOwner && (
        <Link to="/admin" style={{ fontSize: 13, alignSelf: 'flex-start' }}>
          ← All companies
        </Link>
      )}
      <div className="page-head">
        <div className="page-head__titles">
          <h1 className="h1">{company.name}</h1>
          <span className="subtitle">Setup for this company</span>
        </div>
        {isOwner && company.status === 'token_invalid' && (
          <Link className="btn btn--primary" to={`/admin/companies/${id}/fix`}>
            Fix connection
          </Link>
        )}
      </div>
      <nav className="settings-tabs" aria-label="Company setup">
        {COMPANY_TABS.filter((t) => isOwner || !t.ownerOnly).map((t) => (
          <NavLink key={t.path} to={t.path} className={({ isActive }) => (isActive ? 'active' : '')}>
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}

/** Owners land on the company list; managers go straight to their company's setup. */
export function AdminHome({ owner }: { owner: React.ReactNode }) {
  const me = useMe();
  if (!me.data) return <Skeleton height={300} />;
  if (me.data.user.isOwner) return <>{owner}</>;
  return <Navigate to={`/admin/companies/${me.data.company.id}/team`} replace />;
}

/** Old /settings/… addresses go to the current company's setup in Admin. */
export function SettingsRedirect() {
  const { tab = 'team' } = useParams();
  const me = useMe();
  if (!me.data) return <Skeleton height={300} />;
  return <Navigate to={`/admin/companies/${me.data.company.id}/${tab}`} replace />;
}
